import { Box, Divider, IconButton, Typography } from '@mui/material';
import Paper from 'components/paper';

import Tooltip from 'components/tooltip';
import dayjs from 'dayjs';
import { AlertOctagon, Check, Clock, HelpCircle, MoveRight } from 'lucide-react';
import { useMemo } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import _ from 'underscore';
import { useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import { EventName, EventNameToTranslationKey } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings.type';

type Props = {};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan';

type AdjustmentInfo = {
  icon: JSX.Element;
  adjustment: number;
  eventType: EventName;
};

const NextDeliveryBox: React.FC<Props> = () => {
  const intl = useIntl();
  const {
    getOfdSettingsQuery: { data: ofdSettings },
  } = useInputSettingsState();

  const nextDeliveryExplanation = useMemo(() => ofdSettings?.nextDeliveryExplanation || null, [ofdSettings?.nextDeliveryExplanation]);

  const deliveredQuantity = useMemo(() => ofdSettings?.deliveredQuantity || 0, [ofdSettings?.deliveredQuantity]);

  const nextOrderDate = useMemo(() => {
    if (!ofdSettings?.nextDeliveryExplanation) return;

    return dayjs(ofdSettings.nextDeliveryExplanation.utcDate).format('DD MMM');
  }, [ofdSettings?.nextDeliveryExplanation]);

  const deliveryEvent = _.chain(nextDeliveryExplanation?.events).first().value();

  if (!ofdSettings) return null;

  return (
    <Box width={'100%'}>
      <Box display={'flex'} mb={2}>
        <Typography fontWeight={'bold'} width={'100%'}>
          {!nextOrderDate ? (
            'No upcoming orders'
          ) : (
            <>
              <FormattedMessage id={`${PAGE_NAME_ID}.dayOfNextDelivery`} /> ({nextOrderDate})
            </>
          )}
        </Typography>
      </Box>
      <Paper
        transparent
        sx={{ borderRadius: 2, p: 5, height: 220, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {nextDeliveryExplanation === null ? (
          <Box border={'1px solid #f5f5f554'} borderRadius={2} p={2}>
            <Typography display={'flex'} gap={2} alignItems={'center'} mt={5} mb={5}>
              {intl.formatMessage({ id: `${PAGE_NAME_ID}.cards.runOut` })}
            </Typography>
            <Box mt={2} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
              <Box>
                <Typography fontWeight={'bold'} fontSize={23} display={'flex'}>
                  {intl.formatMessage({ id: `${PAGE_NAME_ID}.cards.nextDeliveryNoDate` })}
                  <Box ml={2} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                    <IconButton sx={{ backgroundColor: '#F2B5C0', cursor: 'unset' }} disableRipple disableTouchRipple size="small">
                      <AlertOctagon color="#D61134" size={17} />
                    </IconButton>
                  </Box>
                </Typography>
                <Typography mt={5} mb={5}>
                  {intl.formatMessage({ id: `${PAGE_NAME_ID}.cards.contactCustomerService` })}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box display={'flex'} flexDirection={'column'} gap={4} justifyContent={'center'}>
            <Box display={'flex'} justifyContent={'space-between'} flexDirection={'row'} gap={4}>
              <Box border={'1px solid #f5f5f554'} borderRadius={2} p={2} width={'50%'}>
                <Typography display={'flex'} gap={2} alignItems={'center'}>
                  {intl.formatMessage({ id: `${PAGE_NAME_ID}.cards.startingInventory` })}
                </Typography>
                <Box display={'flex'} flexDirection={'column'} gap={2}>
                  <Typography fontWeight={'bold'} fontSize={23} display={'flex'}>
                    <FormattedNumber
                      value={nextDeliveryExplanation?.startingInventory}
                      unit={'kilogram'}
                      style="unit"
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                    {nextDeliveryExplanation.lowInventory && (
                      <Box ml={2} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                        <IconButton sx={{ backgroundColor: '#F2B5C0', cursor: 'unset' }} disableRipple disableTouchRipple size="small">
                          <AlertOctagon color="#D61134" size={17} />
                        </IconButton>
                      </Box>
                    )}
                  </Typography>

                  {nextDeliveryExplanation.lowInventory ? (
                    <FormattedMessage id={`${PAGE_NAME_ID}.cards.lowInventory`} />
                  ) : (
                    <>
                      (
                      <FormattedNumber
                        value={nextDeliveryExplanation?.startingPercentOfCapacity * 100}
                        unit={'percent'}
                        style="unit"
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                      />
                      &nbsp;
                      <FormattedMessage id={`${PAGE_NAME_ID}.cards.capacity`} />)
                    </>
                  )}
                </Box>
              </Box>
              <Box display={'flex'} alignItems={'center'}>
                <MoveRight size={24} />
              </Box>
              <Box border={'1px solid #f5f5f554'} borderRadius={2} p={2} width={'50%'}>
                <Typography display={'flex'} gap={2} alignItems={'center'}>
                  {intl.formatMessage({ id: `${PAGE_NAME_ID}.cards.endingInventory` })}
                </Typography>
                <Box display={'flex'} flexDirection={'column'} gap={2}>
                  <Typography fontWeight={'bold'} fontSize={23} display={'flex'} alignItems={'center'} gap={2} flexDirection={'row'}>
                    <FormattedNumber
                      value={nextDeliveryExplanation?.endingInventory}
                      unit={'kilogram'}
                      style="unit"
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                    <Tooltip
                      title={
                        <>
                          <Typography fontWeight={'bold'} mb={2}>
                            {intl.formatMessage({ id: `${PAGE_NAME_ID}.tooltipContent` })}
                            <FormattedDate value={nextDeliveryExplanation.utcDate} year="numeric" month="long" day="2-digit" />
                          </Typography>
                          <Box display={'flex'} justifyContent={'space-between'}>
                            <Typography>{intl.formatMessage({ id: `${PAGE_NAME_ID}.cards.startingInventory` })}:</Typography>
                            <Typography>
                              <FormattedNumber
                                value={nextDeliveryExplanation?.startingInventory}
                                unit={'kilogram'}
                                style="unit"
                                minimumFractionDigits={0}
                                maximumFractionDigits={0}
                              />
                            </Typography>
                          </Box>
                          {nextDeliveryExplanation.events?.map((event, idx) => (
                            <Box display={'flex'} justifyContent={'space-between'} key={idx}>
                              <Typography>
                                <FormattedMessage
                                  id={`${PAGE_NAME_ID}.feedInventory.tooltip.events.${EventNameToTranslationKey[event.event]}`}
                                />
                                :
                              </Typography>
                              <FormattedNumber
                                value={event.adjustment}
                                unit={'kilogram'}
                                style="unit"
                                minimumFractionDigits={0}
                                maximumFractionDigits={0}
                              />
                            </Box>
                          )) ?? 0}
                          <Divider sx={{ mt: 2, mb: 2 }} color="#809198" />
                          <Box display={'flex'} justifyContent={'space-between'}>
                            <Typography>{intl.formatMessage({ id: `${PAGE_NAME_ID}.cards.endingInventory` })}:</Typography>
                            <Typography>
                              <FormattedNumber
                                value={nextDeliveryExplanation.endingInventory}
                                unit={'kilogram'}
                                style="unit"
                                minimumFractionDigits={0}
                                maximumFractionDigits={0}
                              />
                            </Typography>
                          </Box>
                        </>
                      }
                      placement={'top'}>
                      <HelpCircle size={18} />
                    </Tooltip>
                  </Typography>
                  <Typography>
                    (
                    <FormattedNumber
                      value={nextDeliveryExplanation?.endingPercentOfCapacity * 100}
                      unit={'percent'}
                      style="unit"
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                    &nbsp;
                    {`${intl.formatMessage({
                      id: `${PAGE_NAME_ID}.cards.capacity`,
                    })})`}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
              <Box display={'flex'} gap={2}>
                {/* Rework to account for delivery and forecast events from a single object */}

                {deliveryEvent?.event === EventName.DELIVERY && (
                  <>
                    <IconButton sx={{ backgroundColor: '#97D7CA', cursor: 'unset' }} disableRipple disableTouchRipple size="small">
                      <Check color="black" size={17} />
                    </IconButton>
                    <FormattedMessage id={`${PAGE_NAME_ID}.cards.confirmedDelivery`} />
                    <Typography fontWeight={'bold'}>
                      <FormattedNumber
                        value={deliveryEvent.adjustment}
                        unit={'kilogram'}
                        style="unit"
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                      />
                    </Typography>
                  </>
                )}
                {deliveryEvent?.event === EventName.FORECAST && (
                  <>
                    <IconButton sx={{ backgroundColor: '#FFEAD6', cursor: 'unset' }} disableRipple disableTouchRipple size="small">
                      <Clock color="black" fill="#FFEAD6" size={17} />
                    </IconButton>
                    <FormattedMessage id={`${PAGE_NAME_ID}.cards.forecastedDelivery`} />
                    <Typography fontWeight={'bold'}>
                      <FormattedNumber
                        value={deliveryEvent.adjustment}
                        unit={'kilogram'}
                        style="unit"
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                      />
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default NextDeliveryBox;
